import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, type LayoutChangeEvent } from "react-native";
import { LegendList, type LegendListRef } from "@legendapp/list";
import { Card, Text, View } from "@src/components";
import type { WasteSenseMapSite } from "@visioni/a3s-reports/client-messages";
import { formatSiteHeading } from "../siteDisplay";
import { SiteLocationsMap } from "./SiteLocationsMap";

interface SitesPanelProps {
  /** Map sites — drives both the side list and the marker layer. */
  mapSites: readonly WasteSenseMapSite[];
  /**
   * External focus request. Each new `{ siteId, key }` value (the `key` lets
   * the same `siteId` re-trigger focus) opens + activates the matching row,
   * scrolls the list to it, and pans the map.
   */
  focusRequest?: { siteId: string; key: number } | null;
}

/** Fixed viewport for the sites list and its companion map (side-by-side only). */
const SITES_PANEL_HEIGHT = 540;

/** Min widths of the two panes + the `gap="md"` (8px) between them. Below the
 *  sum, the row wraps (map drops under the list) — the point at which we also
 *  drop the list's fixed height so it takes only the room its rows need. */
const LIST_MIN_WIDTH = 280;
const MAP_MIN_WIDTH = 320;
const PANEL_GAP = 8;
const STACK_BELOW_WIDTH = LIST_MIN_WIDTH + MAP_MIN_WIDTH + PANEL_GAP;

/**
 * Average list-item height. Collapsed rows are ~80–96px; expanded rows
 * pass ~180px. Bias toward the collapsed case (steady state); LegendList
 * remeasures when rows expand.
 */
const ESTIMATED_ITEM_HEIGHT = 96;

/**
 * "Sites" section — site list paired with an interactive Leaflet map. When the
 * two panes sit side-by-side they share a fixed height so the report doesn't
 * stretch vertically with the site count and the list scrolls internally next
 * to the tall map. When the container is too narrow the panes stack (map under
 * list); there the list drops its fixed height and renders at natural content
 * height, so a report with only a few sites doesn't leave a tall empty gap
 * before the map. The list is virtualised only in the side-by-side (fixed
 * height) case; stacked it's a plain column (few rows, no viewport to fill).
 * Row state (expanded, active) is owned by this parent so `recycleItems` can
 * safely reuse row components.
 *
 * The redesign (2026-07) makes markers per-**site** (one `WasteSenseMapSite`
 * per site), so everything keys on `site_id` and the expanded row shows the
 * three per-site media/dump counts instead of the old observation totals.
 */
export function SitesPanel({ mapSites, focusRequest }: SitesPanelProps) {
  const [activeSiteId, setActiveSiteId] = useState<string | null>(null);
  const [openSiteId, setOpenSiteId] = useState<string | null>(null);
  const listRef = useRef<LegendListRef>(null);

  // Measure the section width so the stacked (wrapped) state can render the list
  // at natural height instead of the fixed side-by-side viewport. Matches the
  // `flexWrap` wrap point (list + map min widths + gap). Assume side-by-side
  // until first measure (the common desktop case) to avoid a layout flash.
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);
  const stacked = containerWidth !== null && containerWidth < STACK_BELOW_WIDTH;

  // Sort by suburb → address so geographically-clustered sites group
  // together in the list, matching how operators scan the report.
  const sortedSites = useMemo(() => {
    return [...mapSites].sort((a, b) => {
      const ah = formatSiteHeading(a.site_title);
      const bh = formatSiteHeading(b.site_title);
      return ah.localeCompare(bh);
    });
  }, [mapSites]);

  // site_id → list index, so a marker click can `scrollToIndex` without
  // scanning sites on every interaction.
  const indexBySiteId = useMemo(() => {
    const m = new Map<string, number>();
    sortedSites.forEach((s, i) => m.set(s.site_id, i));
    return m;
  }, [sortedSites]);

  const handleToggle = useCallback((siteId: string) => {
    setOpenSiteId((current) => (current === siteId ? null : siteId));
    setActiveSiteId(siteId);
  }, []);

  /**
   * Marker → list direction: open + activate the row AND scroll it
   * into view. We bias the target position toward the top
   * (`viewPosition: 0.1`) so the just-opened row's expanded body is
   * comfortably visible inside the fixed-height pane.
   */
  const handleSelectFromMap = useCallback(
    (siteId: string) => {
      setOpenSiteId(siteId);
      setActiveSiteId(siteId);
      const index = indexBySiteId.get(siteId);
      if (index !== undefined) {
        listRef.current?.scrollToIndex({ index, animated: true, viewPosition: 0.1 });
      }
    },
    [indexBySiteId],
  );

  /**
   * External focus → same machinery as the marker click. The parent
   * passes a `key` that increments on every focus request, so requesting
   * focus on the same row twice still re-pans the map and re-scrolls.
   */
  useEffect(() => {
    if (!focusRequest) return;
    handleSelectFromMap(focusRequest.siteId);
  }, [focusRequest, handleSelectFromMap]);

  const renderItem = useCallback(
    ({ item }: { item: WasteSenseMapSite }) => (
      <View pb="sm">
        <SitesPanelItem
          site={item}
          isActive={item.site_id === activeSiteId}
          isOpen={item.site_id === openSiteId}
          onToggle={handleToggle}
        />
      </View>
    ),
    [activeSiteId, openSiteId, handleToggle],
  );

  const keyExtractor = useCallback((item: WasteSenseMapSite) => item.site_id, []);

  return (
    <View gap="md" onLayout={onLayout}>
      <Text textStyle="h4" color="text">
        Sites
      </Text>

      <View flexDirection="row" gap="md" flexWrap="wrap">
        {/* List pane. Side-by-side: fixed-height virtualised viewport that
            scrolls beside the tall map. Stacked: a plain natural-height column
            so a short site list doesn't leave a tall gap before the map. */}
        {stacked ?
          <View flex={1} minWidth={LIST_MIN_WIDTH}>
            {sortedSites.map((item) => (
              <View pb="sm" key={item.site_id}>
                <SitesPanelItem
                  site={item}
                  isActive={item.site_id === activeSiteId}
                  isOpen={item.site_id === openSiteId}
                  onToggle={handleToggle}
                />
              </View>
            ))}
          </View>
        : <View flex={1} minWidth={LIST_MIN_WIDTH} height={SITES_PANEL_HEIGHT}>
            <LegendList
              ref={listRef}
              data={sortedSites as WasteSenseMapSite[]}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              extraData={`${activeSiteId ?? ""}|${openSiteId ?? ""}`}
              estimatedItemSize={ESTIMATED_ITEM_HEIGHT}
              recycleItems
              drawDistance={ESTIMATED_ITEM_HEIGHT * 4}
              showsVerticalScrollIndicator
              style={{ flex: 1 }}
            />
          </View>
        }

        <View flex={2} minWidth={MAP_MIN_WIDTH} height={SITES_PANEL_HEIGHT}>
          <SiteLocationsMap
            sites={mapSites}
            focusSiteId={activeSiteId}
            focusRequest={focusRequest ?? null}
            onSelectSite={handleSelectFromMap}
          />
        </View>
      </View>
    </View>
  );
}

interface SitesPanelItemProps {
  site: WasteSenseMapSite;
  isActive: boolean;
  isOpen: boolean;
  onToggle: (siteId: string) => void;
}

function SitesPanelItem({ site, isActive, isOpen, onToggle }: SitesPanelItemProps) {
  return (
    <Card px="md" py="md" borderColor={isActive ? "danger" : "borderCard"}>
      <Pressable
        onPress={() => onToggle(site.site_id)}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
      >
        <View flexDirection="row" alignItems="center" gap="md">
          <View flex={1} gap="xxs">
            <Text textStyle="sm/semibold" color="text">
              {formatSiteHeading(site.site_title)}
            </Text>
          </View>
        </View>
      </Pressable>

      {isOpen ?
        <View gap="xs" mt="md">
          <Divider />
          <Meta label="Clips recorded" value={site.clips_recorded.toLocaleString()} />
          <Meta label="Snapshots captured" value={site.snapshots_captured.toLocaleString()} />
          <Meta label="Confirmed dumps" value={site.confirmed_dumps.toLocaleString()} />
        </View>
      : null}
    </Card>
  );
}

interface MetaProps {
  label: string;
  value: string;
}

function Meta({ label, value }: MetaProps) {
  return (
    <View flexDirection="row" justifyContent="space-between" gap="md">
      <Text textStyle="xs/regular" color="textMuted">
        {label}
      </Text>
      <Text textStyle="xs/semibold" color="text">
        {value}
      </Text>
    </View>
  );
}

function Divider() {
  return <View h={1} bg="borderCard" />;
}
